import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FieldErrors, FormState } from 'react-hook-form';
import { debounce } from '@cian/ui-kit/internal/utils';

import { useLatest } from 'shared/common/hooks/useLatest';
import { EElementCategory, IFieldElement, IFormState, IScheme, ISelectedRegion, TElement } from '../types/calculator';
import { ECalculatorEventName, dispatchWidgetEvent } from 'shared/common/utils';
import { ECustomEventsDescriptions } from '../constants/customEventsDescriptions';
import { selectSelectedRegion } from '../selectors';
import { buildOfferCalculatorSubmitPayload, getEnrichedErrors } from '../utils';

const WIDGET_EVENT_DEBOUNCE_MS = 300;
let activeFormTarget: HTMLFormElement | null = null;

export interface IUseCalculatorCustomEventsDeps {
  getValues: () => IFormState;
  /** useWatch в deps — ре-запуск эффекта при любом поле. */
  watchedFormValues: IFormState;
  isValid: boolean;
  isDirty: boolean;
  errors: FormState<IFormState>['errors'];
  dirtyFields: FormState<IFormState>['dirtyFields'];
  touchedFields: FormState<IFormState>['touchedFields'];
  submitCount: number;
  eventTarget: HTMLFormElement | null;
  formScheme: TElement[];
}

const getFieldNamesWithValidation = (formScheme: IScheme[]) => {
  return formScheme[0].elements
    .filter(elem => elem.category === EElementCategory.Field && elem.validation)
    .map((elem: IFieldElement) => elem.name);
};

export const useCalculatorCustomEvents = ({
  getValues,
  watchedFormValues,
  isValid,
  errors,
  isDirty,
  dirtyFields,
  touchedFields,
  submitCount,
  eventTarget,
  formScheme,
}: IUseCalculatorCustomEventsDeps) => {
  const selectedRegion = useSelector(selectSelectedRegion);

  const getValuesRef = useLatest(getValues);
  const selectedRegionRef = useLatest(selectedRegion);
  const isValidRef = useLatest(isValid);
  const errorsRef = useLatest(errors);
  const formSchemeRef = useLatest(formScheme);
  const eventTargetRef = useLatest(eventTarget);
  const flushRequestIdRef = useRef(0);

  const debouncedFlush = useMemo(
    () =>
      debounce((requestId: number) => {
        // Игнорируем устаревшие отложенные вызовы — отправляем только последний запрошенный flush.
        if (requestId !== flushRequestIdRef.current) {
          return;
        }
        const target = eventTargetRef.current;
        if (!target) {
          return;
        }
        if (activeFormTarget != null && activeFormTarget !== target) {
          return;
        }
        if (isValidRef.current) {
          const e = errorsRef.current;
          if (Object.keys(e).length > 0) {
            return;
          }
          const region: ISelectedRegion | null = selectedRegionRef.current;
          const payload = buildOfferCalculatorSubmitPayload(getValuesRef.current() as IFormState, region);
          dispatchWidgetEvent(target, ECalculatorEventName.onSubmit, {
            description: ECustomEventsDescriptions.onSubmit,
            data: payload,
          });
        } else {
          const currentErrors: FieldErrors<IFormState> = errorsRef.current;
          if (Object.keys(currentErrors).length === 0) {
            return;
          }
          const scheme = formSchemeRef.current;
          const enriched = getEnrichedErrors(currentErrors, scheme as IScheme[]);
          const order = getFieldNamesWithValidation(scheme as IScheme[]);
          const sorted = [...enriched].sort((a, b) => order.indexOf(a.field) - order.indexOf(b.field));
          dispatchWidgetEvent(target, ECalculatorEventName.onError, {
            description: ECustomEventsDescriptions.onError,
            data: sorted as unknown as FieldErrors<IFormState>,
          });
        }
      }, WIDGET_EVENT_DEBOUNCE_MS),
    [],
  );

  const runFlush = useCallback(() => {
    flushRequestIdRef.current += 1;
    void debouncedFlush(flushRequestIdRef.current);
  }, [debouncedFlush]);

  useEffect(() => {
    const target = eventTargetRef.current;
    if (!target) {
      return;
    }

    const markActive = () => {
      activeFormTarget = target;
    };

    if (activeFormTarget == null) {
      activeFormTarget = target;
    }

    target.addEventListener('focusin', markActive);
    target.addEventListener('input', markActive);
    target.addEventListener('change', markActive);

    return () => {
      target.removeEventListener('focusin', markActive);
      target.removeEventListener('input', markActive);
      target.removeEventListener('change', markActive);

      if (activeFormTarget === target) {
        activeFormTarget = null;
      }
    };
  }, [eventTarget]);

  useEffect(() => {
    return () => {
      debouncedFlush.cancel();
    };
  }, [debouncedFlush]);

  useEffect(() => {
    runFlush();
  }, [
    getValues,
    watchedFormValues,
    isValid,
    selectedRegion,
    errors,
    isDirty,
    dirtyFields,
    touchedFields,
    submitCount,
    eventTarget,
    formScheme,
    runFlush,
  ]);
};
